import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';

const ChatContext = createContext(null);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within ChatProvider');
  return context;
};

const getTodayDate = () => new Date().toISOString().split('T')[0];

export const ChatProvider = ({ children }) => {
  const { user } = useAuth();
  const [rooms, setRooms] = useState({ channels: [], direct_messages: [], users: [] });
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(getTodayDate());
  const pollIntervalRef = useRef(null);

  // Fetch rooms
  const fetchRooms = useCallback(async () => {
    if (!user) return;
    try {
      const response = await api.get('/chat/rooms');
      setRooms(response.data || { channels: [], direct_messages: [], users: [] });
      
      // Set default room
      if (!currentRoom && response.data?.channels?.length > 0) {
        setCurrentRoom(response.data.channels[0]);
      }
    } catch (err) {
      console.log('Chat: Error fetching rooms', err.message);
    }
  }, [user, currentRoom]);

  // Fetch messages for current room
  const fetchMessages = useCallback(async () => {
    if (!user || !currentRoom) return;
    
    try {
      const response = await api.get(`/chat/messages/${currentRoom.id}?date=${currentDate}`);
      const newMessages = response.data?.messages || [];
      
      if (!chatOpen && messages.length > 0 && newMessages.length > messages.length) {
        setUnreadCount(prev => prev + (newMessages.length - messages.length));
      }
      
      setMessages(newMessages);
    } catch (err) {
      console.log('Chat: Error fetching messages', err.message);
    } finally {
      setLoading(false);
    }
  }, [user, currentRoom, currentDate, chatOpen, messages.length]);

  // Send message
  const sendMessage = async (messageText) => {
    if (!user || !messageText.trim() || !currentRoom) return;

    try {
      const response = await api.post(`/chat/messages/${currentRoom.id}`, {
        message: messageText.trim(),
        room_id: currentRoom.id
      });
      
      if (response.data) {
        setMessages(prev => [...prev, response.data]);
      }
      return response.data;
    } catch (err) {
      console.error('Send message error:', err);
      throw err;
    }
  };

  // Archive today's chat
  const archiveToday = async () => {
    if (!currentRoom) return;
    try {
      const response = await api.post(`/chat/archive/${currentDate}?room_id=${currentRoom.id}`);
      return response.data;
    } catch (err) {
      console.error('Archive error:', err);
      throw err;
    }
  };

  // Check date at midnight
  useEffect(() => {
    const checkDate = () => {
      const today = getTodayDate();
      if (today !== currentDate) {
        setCurrentDate(today);
        setMessages([]);
      }
    };
    const interval = setInterval(checkDate, 60000);
    return () => clearInterval(interval);
  }, [currentDate]);

  // Initial fetch
  useEffect(() => {
    if (user) {
      fetchRooms();
    }
  }, [user]);

  // Fetch messages when room changes
  useEffect(() => {
    if (currentRoom) {
      setLoading(true);
      fetchMessages();
    }
  }, [currentRoom, currentDate]);

  // Poll for new messages
  useEffect(() => {
    if (user && currentRoom) {
      pollIntervalRef.current = setInterval(fetchMessages, 3000);
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [user, currentRoom, fetchMessages]);

  // Clear unread on open
  useEffect(() => {
    if (chatOpen) {
      setUnreadCount(0);
    }
  }, [chatOpen]);

  return (
    <ChatContext.Provider value={{
      rooms,
      currentRoom,
      setCurrentRoom,
      messages,
      loading,
      sendMessage,
      unreadCount,
      chatOpen,
      setChatOpen,
      currentDate,
      archiveToday,
      refreshMessages: fetchMessages,
      refreshRooms: fetchRooms
    }}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
